// Shared helpers to build "Add to Google Calendar" URLs and downloadable
// .ics data URLs for appointment-related emails (booking, confirmation,
// reminders, reschedules). Keep the output identical to BookAppointment.tsx
// so every email surfaces the same calendar event.

export interface CalendarEventInput {
  /** Event start (UTC instant). */
  start: Date;
  /** Event end (UTC instant). */
  end: Date;
  /** Calendar event title — e.g. "Elara Health Consultation with Dr. Smith". */
  title: string;
  /** Multi-line description shown in the calendar event body. */
  description: string;
  /** Location string — usually the meeting URL. */
  location: string;
}

const toCalDateTime = (d: Date) =>
  d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

export function buildGoogleCalendarUrl(event: CalendarEventInput): string {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${toCalDateTime(event.start)}/${toCalDateTime(event.end)}`,
    details: event.description,
    location: event.location,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function buildIcsDataUrl(event: CalendarEventInput): string {
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Elara Health//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `DTSTART:${toCalDateTime(event.start)}`,
    `DTEND:${toCalDateTime(event.end)}`,
    `SUMMARY:${event.title}`,
    `DESCRIPTION:${event.description.replace(/\n/g, "\\n")}`,
    `LOCATION:${event.location}`,
    `URL:${event.location}`,
    `STATUS:CONFIRMED`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

/**
 * Convenience builder for the standard "Elara Health consultation" event
 * description used across all appointment emails. Pass already-formatted
 * date/time strings so the description matches what the recipient sees in
 * the email body.
 */
export function buildConsultationEventStrings(opts: {
  providerName?: string | null;
  callType: string | null | undefined;
  meetingUrl: string;
  appointmentDate: string;
  appointmentTime: string;
  dashboardUrl?: string;
}): { title: string; description: string } {
  const callLabel = opts.callType === "audio" ? "Audio" : "Video";
  const provider = opts.providerName?.trim() || "Your Provider";
  const description = [
    `${callLabel} consultation via Elara Health`,
    ``,
    `Provider: ${provider}`,
    `Date: ${opts.appointmentDate}`,
    `Time: ${opts.appointmentTime}`,
    ``,
    `Join your ${callLabel.toLowerCase()} consultation:`,
    opts.meetingUrl,
    ...(opts.dashboardUrl
      ? [``, `Log in to your Elara Health dashboard:`, opts.dashboardUrl]
      : []),
  ].join("\n");

  return {
    title: `Elara Health Consultation${opts.providerName ? ` with ${provider}` : ""}`,
    description,
  };
}
